/**
 * CSV Parser - Flexible roster import utility
 * Handles various column names and formats common in XC timing
 */

// Common column name aliases for flexible parsing
const COLUMN_ALIASES = {
  bib: ['bib', 'bib_number', 'bib number', 'race #', 'race#', 'race number', 'number', '#'],
  firstName: ['first', 'first name', 'first_name', 'firstname', 'given name'],
  lastName: ['last', 'last name', 'last_name', 'lastname', 'surname', 'family name'],
  gender: ['gender', 'sex', 'g', 'm/f'],
  grade: ['grade', 'gr', 'year', 'class'],
  age: ['age', 'years'],
  ageGroup: ['age group', 'age_group', 'agegroup', 'age-group', 'ag', 'age grp', 'agegrp'],
  division: ['division', 'div', 'category', 'cat', 'level', 'varsity/jv', 'race division'],
  team: ['team', 'team_name', 'team name', 'school', 'club', 'organization', 'org']
}

/**
 * Parse CSV text into array of athlete objects
 * @param {string} csvText - Raw CSV content
 * @param {Object} options - Parsing options
 * @returns {Object} { athletes: [], errors: [], warnings: [] }
 */
export function parseCSV(csvText, options = {}) {
  const {
    delimiter = null, // Auto-detect if null
    hasHeader = true
  } = options

  const result = {
    athletes: [],
    errors: [],
    warnings: [],
    columnMapping: {}
  }

  if (!csvText || !csvText.trim()) {
    result.errors.push('CSV file is empty')
    return result
  }

  // Split into lines, handling different line endings
  const lines = csvText.trim().split(/\r?\n/)

  if (lines.length < 2) {
    result.errors.push('CSV must have at least a header row and one data row')
    return result
  }

  // Auto-detect delimiter
  const detectedDelimiter = delimiter || detectDelimiter(lines[0])

  // Parse header row
  const headers = parseLine(lines[0], detectedDelimiter).map(h => h.toLowerCase().trim())

  // Map headers to our standard fields
  result.columnMapping = mapColumns(headers)

  // Check for required fields
  if (result.columnMapping.bib === -1) {
    result.errors.push('Missing required column: Bib/Race # (tried: ' + COLUMN_ALIASES.bib.join(', ') + ')')
    return result
  }

  // At least one name field is required
  if (result.columnMapping.firstName === -1 && result.columnMapping.lastName === -1) {
    result.errors.push('Missing name column: Need at least First Name or Last Name')
    return result
  }

  // Parse data rows
  for (let i = 1; i < lines.length; i++) {
    const line = lines[i].trim()
    if (!line) continue // Skip empty lines

    const values = parseLine(line, detectedDelimiter)
    const lineNum = i + 1

    try {
      const athlete = parseAthleteRow(values, result.columnMapping, lineNum)

      if (athlete.bib === null || athlete.bib === undefined) {
        result.warnings.push(`Row ${lineNum}: Skipped - no bib number`)
        continue
      }

      // Validate bib is a number
      if (isNaN(athlete.bib)) {
        result.warnings.push(`Row ${lineNum}: Invalid bib "${values[result.columnMapping.bib]}"`)
        continue
      }

      result.athletes.push(athlete)
    } catch (e) {
      result.errors.push(`Row ${lineNum}: ${e.message}`)
    }
  }

  // Check for duplicate bibs
  const bibCounts = {}
  result.athletes.forEach(a => {
    bibCounts[a.bib] = (bibCounts[a.bib] || 0) + 1
  })
  Object.entries(bibCounts).forEach(([bib, count]) => {
    if (count > 1) {
      result.warnings.push(`Duplicate bib number: ${bib} appears ${count} times`)
    }
  })

  return result
}

/**
 * Detect CSV delimiter (comma, tab, semicolon)
 */
function detectDelimiter(line) {
  const delimiters = [',', '\t', ';', '|']
  const counts = delimiters.map(d => ({
    delimiter: d,
    count: (line.match(new RegExp(d === '|' ? '\\|' : d, 'g')) || []).length
  }))

  // Return delimiter with highest count
  const best = counts.reduce((a, b) => b.count > a.count ? b : a)
  return best.count > 0 ? best.delimiter : ','
}

/**
 * Parse a single CSV line, handling quoted values
 */
function parseLine(line, delimiter) {
  const values = []
  let current = ''
  let inQuotes = false

  for (let i = 0; i < line.length; i++) {
    const char = line[i]

    if (char === '"') {
      if (inQuotes && line[i + 1] === '"') {
        // Escaped quote
        current += '"'
        i++
      } else {
        inQuotes = !inQuotes
      }
    } else if (char === delimiter && !inQuotes) {
      values.push(current.trim())
      current = ''
    } else {
      current += char
    }
  }

  values.push(current.trim())
  return values
}

/**
 * Map header names to column indices
 */
function mapColumns(headers) {
  const mapping = {
    bib: -1,
    firstName: -1,
    lastName: -1,
    gender: -1,
    grade: -1,
    age: -1,
    ageGroup: -1,
    division: -1,
    team: -1
  }

  for (const [field, aliases] of Object.entries(COLUMN_ALIASES)) {
    for (let i = 0; i < headers.length; i++) {
      const header = headers[i]
      if (aliases.some(alias => header === alias || header.includes(alias))) {
        mapping[field] = i
        break
      }
    }
  }

  return mapping
}

/**
 * Parse a data row into athlete object
 */
function parseAthleteRow(values, mapping, lineNum) {
  const getValue = (field) => {
    const idx = mapping[field]
    return idx >= 0 && idx < values.length ? values[idx].trim() : null
  }

  const bibRaw = getValue('bib')
  const bib = bibRaw ? parseInt(bibRaw, 10) : null

  // Parse gender - normalize to M/F/NB
  let gender = getValue('gender')
  if (gender) {
    gender = gender.toUpperCase()
    if (gender.startsWith('M') || gender === 'MALE') gender = 'M'
    else if (gender.startsWith('F') || gender === 'FEMALE') gender = 'F'
    else if (gender === 'NB' || gender === 'NON-BINARY') gender = 'NB'
  }

  // Parse grade/age
  const gradeRaw = getValue('grade')
  const grade = gradeRaw ? parseInt(gradeRaw, 10) : null

  const ageRaw = getValue('age')
  const age = ageRaw ? parseInt(ageRaw, 10) : null

  return {
    bib,
    firstName: getValue('firstName') || '',
    lastName: getValue('lastName') || '',
    gender: gender || null,
    grade: isNaN(grade) ? null : grade,
    age: isNaN(age) ? null : age,
    ageGroup: getValue('ageGroup') || null,
    division: getValue('division') || null,
    team: getValue('team') || null
  }
}

/**
 * Generate CSV from athletes array
 * @param {Array} athletes - Array of athlete objects
 * @returns {string} CSV content
 */
export function generateCSV(athletes) {
  const headers = ['Bib', 'First Name', 'Last Name', 'Gender', 'Grade', 'Age', 'Team']
  const lines = [headers.join(',')]

  for (const a of athletes) {
    const row = [
      a.bib,
      escapeCSV(a.firstName),
      escapeCSV(a.lastName),
      a.gender || '',
      a.grade || '',
      a.age || '',
      escapeCSV(a.team || '')
    ]
    lines.push(row.join(','))
  }

  return lines.join('\n')
}

/**
 * Escape a value for CSV (quote if contains comma/quote/newline)
 */
function escapeCSV(value) {
  if (!value) return ''
  const str = String(value)
  if (str.includes(',') || str.includes('"') || str.includes('\n')) {
    return '"' + str.replace(/"/g, '""') + '"'
  }
  return str
}

/**
 * Validate an athlete object
 */
export function validateAthlete(athlete) {
  const errors = []

  if (!athlete.bib || isNaN(athlete.bib)) {
    errors.push('Invalid or missing bib number')
  }

  if (!athlete.firstName && !athlete.lastName) {
    errors.push('Name is required (first or last)')
  }

  if (athlete.gender && !['M', 'F', 'NB'].includes(athlete.gender)) {
    errors.push('Invalid gender (use M, F, or NB)')
  }

  if (athlete.grade && (athlete.grade < 1 || athlete.grade > 12)) {
    errors.push('Grade should be 1-12')
  }

  if (athlete.age && (athlete.age < 5 || athlete.age > 100)) {
    errors.push('Age seems invalid')
  }

  return errors
}

/**
 * Get display name for an athlete
 */
export function getAthleteName(athlete) {
  if (athlete.firstName && athlete.lastName) {
    return `${athlete.firstName} ${athlete.lastName}`
  }
  return athlete.firstName || athlete.lastName || `Bib #${athlete.bib}`
}

/**
 * Get raw CSV data for column mapping UI
 * @param {string} csvText - Raw CSV content
 * @returns {Object} { headers: [], rows: [], detectedMapping: {}, delimiter: '' }
 */
export function getCSVHeaders(csvText) {
  const result = {
    headers: [],
    rows: [],
    sampleRows: [],
    detectedMapping: {},
    delimiter: ','
  }

  if (!csvText || !csvText.trim()) {
    return result
  }

  const lines = csvText.trim().split(/\r?\n/)
  if (lines.length < 1) {
    return result
  }

  result.delimiter = detectDelimiter(lines[0])
  result.headers = parseLine(lines[0], result.delimiter).map(h => h.trim())

  // Get normalized headers for auto-mapping
  const normalizedHeaders = result.headers.map(h => h.toLowerCase())
  result.detectedMapping = mapColumns(normalizedHeaders)

  // Get sample rows (up to 5)
  for (let i = 1; i < Math.min(lines.length, 6); i++) {
    const line = lines[i].trim()
    if (line) {
      result.sampleRows.push(parseLine(line, result.delimiter))
    }
  }

  // Get all data rows
  for (let i = 1; i < lines.length; i++) {
    const line = lines[i].trim()
    if (line) {
      result.rows.push(parseLine(line, result.delimiter))
    }
  }

  return result
}

/**
 * Parse CSV with custom column mapping
 * @param {string} csvText - Raw CSV content
 * @param {Object} columnMapping - Manual column mapping { field: columnIndex }
 * @returns {Object} { athletes: [], errors: [], warnings: [] }
 */
export function parseCSVWithMapping(csvText, columnMapping) {
  const result = {
    athletes: [],
    errors: [],
    warnings: [],
    columnMapping
  }

  if (!csvText || !csvText.trim()) {
    result.errors.push('CSV file is empty')
    return result
  }

  const lines = csvText.trim().split(/\r?\n/)
  if (lines.length < 2) {
    result.errors.push('CSV must have at least a header row and one data row')
    return result
  }

  const delimiter = detectDelimiter(lines[0])

  // Check for required fields
  if (columnMapping.bib === -1 || columnMapping.bib === undefined) {
    result.errors.push('Please map a column to Bib/Race #')
    return result
  }

  // At least one name field is required
  if ((columnMapping.firstName === -1 || columnMapping.firstName === undefined) &&
      (columnMapping.lastName === -1 || columnMapping.lastName === undefined)) {
    result.errors.push('Please map at least a First Name or Last Name column')
    return result
  }

  // Parse data rows
  for (let i = 1; i < lines.length; i++) {
    const line = lines[i].trim()
    if (!line) continue

    const values = parseLine(line, delimiter)
    const lineNum = i + 1

    try {
      const athlete = parseAthleteRow(values, columnMapping, lineNum)

      if (athlete.bib === null || athlete.bib === undefined) {
        result.warnings.push(`Row ${lineNum}: Skipped - no bib number`)
        continue
      }

      if (isNaN(athlete.bib)) {
        result.warnings.push(`Row ${lineNum}: Invalid bib "${values[columnMapping.bib]}"`)
        continue
      }

      result.athletes.push(athlete)
    } catch (e) {
      result.errors.push(`Row ${lineNum}: ${e.message}`)
    }
  }

  // Check for duplicate bibs
  const bibCounts = {}
  result.athletes.forEach(a => {
    bibCounts[a.bib] = (bibCounts[a.bib] || 0) + 1
  })
  Object.entries(bibCounts).forEach(([bib, count]) => {
    if (count > 1) {
      result.warnings.push(`Duplicate bib number: ${bib} appears ${count} times`)
    }
  })

  return result
}

/**
 * Get field display name
 */
export function getFieldDisplayName(field) {
  const names = {
    bib: 'Bib #',
    firstName: 'First Name',
    lastName: 'Last Name',
    gender: 'Gender',
    grade: 'Grade',
    age: 'Age',
    ageGroup: 'Age Group',
    division: 'Division',
    team: 'Team'
  }
  return names[field] || field
}

/**
 * Get all available fields for mapping
 */
export function getAvailableFields() {
  return ['bib', 'firstName', 'lastName', 'gender', 'grade', 'age', 'ageGroup', 'division', 'team']
}

/**
 * Export column aliases for use in UI
 */
export { COLUMN_ALIASES }
